import React from 'react';
import { useIntl } from 'react-intl';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { CenteredFlexBox, ColumnFlexBox } from '../../FlexBox';
import useTranslate from '../../../Hooks/useTranslate';

/** A dependency as it is held before a sibling has been chosen for it. */
const EMPTY = { field: '', as: '', optional: false };

/** The dependencies worth saving: the ones naming a sibling. */
const composed = (held) => {
  const named = held
    .filter(({ field }) => field)
    .map(({ field, as, optional }) => ({
      field,
      ...(as ? { as } : {}),
      ...(optional ? { optional: true } : {}),
    }));

  return named.length > 0 ? named : undefined;
};

/**
 * Names the siblings a field's option list is asked for.
 *
 * Each dependency says which sibling it reads, the parameter name its procedure answers to, and
 * whether the field may be asked without it. A required one that is unset leaves the field disabled
 * until it is filled in.
 *
 * @param {Object} props - Component props.
 * @param {String} props.label - What the setting is called.
 * @param {Array} props.value - The dependencies held.
 * @param {Array} props.siblings - The entries beside this one, as `{ key, label }`.
 * @param {Function} props.onChange - Receives the new list, undefined where it names nothing.
 *
 * @returns {React.JSX.Element}
 */
const DependsOnField = function DependsOnField({
  label,
  value = null,
  siblings = [],
  onChange = () => {},
}) {
  const intl = useIntl();
  const translate = useTranslate();
  // One is built a control at a time and says nothing until it names a sibling, so the ones being
  // built are held here while only the finished ones are published.
  const [held, setHeld] = React.useState(() => value ?? []);

  const write = (next) => {
    setHeld(next);
    onChange(composed(next));
  };

  const update = (index, changes) =>
    write(held.map((one, at) => (at === index ? { ...one, ...changes } : one)));

  return (
    <ColumnFlexBox gap="8px" sx={{ width: '100%' }}>
      <Typography variant="caption" sx={{ opacity: '70%' }}>
        {label}
      </Typography>
      {held.map((one, index) => (
        <CenteredFlexBox key={`depends-${String(index)}`} gap="8px">
          <Select
            size="small"
            displayEmpty
            value={one.field ?? ''}
            onChange={(e) => update(index, { field: e.target.value })}
            sx={{ flex: 1, minWidth: '120px' }}
          >
            {!one.field && (
              <MenuItem value="">{intl.messages.CHOOSE_A_KEY}</MenuItem>
            )}
            {siblings.map(({ key, label: wording }) => (
              <MenuItem key={key} value={key}>
                {translate(wording || key)}
              </MenuItem>
            ))}
          </Select>
          {/* The name the procedure behind the list answers to, where it differs from the key. */}
          <TextField
            size="small"
            label={intl.messages.PARAMETER_NAME}
            value={one.as ?? ''}
            onChange={(e) => update(index, { as: e.target.value })}
            sx={{ width: '160px', flex: 'none' }}
          />
          <Tooltip arrow title={intl.messages.OPTIONAL_DEPENDENCY}>
            <CenteredFlexBox gap="4px" sx={{ flex: 'none' }}>
              <Switch
                checked={Boolean(one.optional)}
                onChange={(e) => update(index, { optional: e.target.checked })}
              />
              <Typography variant="body2">{intl.messages.OPTIONAL}</Typography>
            </CenteredFlexBox>
          </Tooltip>
          <IconButton
            size="small"
            onClick={() => write(held.filter((_, at) => at !== index))}
          >
            <DeleteRoundedIcon fontSize="small" />
          </IconButton>
        </CenteredFlexBox>
      ))}
      <Box>
        <Button
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={() => write([...held, { ...EMPTY }])}
        >
          {intl.messages.ADD_DEPENDENCY}
        </Button>
      </Box>
    </ColumnFlexBox>
  );
};

export default DependsOnField;
